import React, { useState, useEffect, useCallback } from 'react';
import { usuariosAPI, sucursalesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const Usuarios = () => {
  const { esAdmin, usuario: currentUser } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filtros, setFiltros] = useState({ buscar: '', rol_id: '', activo: '', sucursal_id: '' });
  const [form, setForm] = useState({
    username: '', email: '', password: '', nombre_completo: '',
    cedula: '', telefono: '', rol_id: '', sucursal_id: '', activo: true
  });

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, sucRes] = await Promise.all([
        usuariosAPI.getAll(filtros),
        usuariosAPI.getRoles(),
        sucursalesAPI.getAll()
      ]);
      setUsuarios(usersRes.data.data || []);
      setRoles(rolesRes.data.data || []);
      setSucursales(sucRes.data.data || []);
    } catch {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const resetForm = () => ({
    username: '', email: '', password: '', nombre_completo: '',
    cedula: '', telefono: '', rol_id: '', sucursal_id: '', activo: true
  });

  const openNew = () => {
    setEditing(null);
    setForm(resetForm());
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({
      username: user.username || '',
      email: user.email || '',
      password: '', // no rellenar por seguridad
      nombre_completo: user.nombre_completo || '',
      cedula: user.cedula || '',
      telefono: user.telefono || '',
      rol_id: user.rol_id || '',
      sucursal_id: user.sucursal_id || '',
      activo: user.activo !== false
    });
    setShowModal(true);
  };

  const openDetail = async (user) => {
    try {
      const res = await usuariosAPI.getById(user.id);
      setSelectedUser(res.data.data);
      setShowDetailModal(true);
    } catch {
      toast.error('Error al cargar detalle');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.nombre_completo.trim()) {
      toast.error('Usuario y nombre completo son requeridos');
      return;
    }
    if (!editing && !form.password) {
      toast.error('La contraseña es requerida para nuevos usuarios');
      return;
    }
    if (!form.rol_id) {
      toast.error('Debe asignar un rol');
      return;
    }
    if (!form.sucursal_id) {
      toast.error('Debe asignar una sucursal');
      return;
    }
    if (form.password && form.password.length < 6) {
      toast.error('La contraseña debe tener mínimo 6 caracteres');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password; // no enviar password vacío en edición

      if (editing) {
        await usuariosAPI.update(editing.id, payload);
        toast.success('Usuario actualizado');
      } else {
        await usuariosAPI.create(payload);
        toast.success('Usuario creado exitosamente');
      }
      setShowModal(false);
      cargarDatos();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const toggleActivo = async (user) => {
    try {
      await usuariosAPI.update(user.id, { activo: !user.activo });
      toast.success(user.activo ? 'Usuario desactivado' : 'Usuario activado');
      cargarDatos();
    } catch {
      toast.error('Error al cambiar estado');
    }
  };

  const getRolBadge = (rol) => {
    const colors = {
      super_admin: 'dark', admin: 'danger', gerente: 'warning', vendedor: 'info',
      optometrista: 'success', tecnico_lab: 'secondary', recepcionista: 'primary'
    };
    return colors[rol] || 'secondary';
  };

  const getRolLabel = (rol) => {
    const labels = {
      super_admin: 'Super Admin', admin: 'Administrador', gerente: 'Gerente', vendedor: 'Vendedor',
      optometrista: 'Optometrista', tecnico_lab: 'Técnico Lab', recepcionista: 'Recepcionista'
    };
    return labels[rol] || rol;
  };

  const stats = {
    total: usuarios.length,
    activos: usuarios.filter(u => u.activo).length,
    admins: usuarios.filter(u => u.rol_nombre === 'admin').length
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1 fw-bold">
            <i className="bi bi-people-fill me-2 text-primary"></i>
            Gestión de Usuarios
          </h4>
          <p className="text-muted mb-0">Administra empleados, roles y accesos al sistema</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>
          <i className="bi bi-person-plus-fill me-1"></i> Nuevo Usuario
        </button>
      </div>

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card border-0 bg-primary bg-opacity-10">
            <div className="card-body py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small">Total Usuarios</div>
                  <div className="h4 mb-0 fw-bold text-primary">{stats.total}</div>
                </div>
                <i className="bi bi-people display-6 text-primary opacity-50"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 bg-success bg-opacity-10">
            <div className="card-body py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small">Activos</div>
                  <div className="h4 mb-0 fw-bold text-success">{stats.activos}</div>
                </div>
                <i className="bi bi-person-check display-6 text-success opacity-50"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 bg-danger bg-opacity-10">
            <div className="card-body py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small">Administradores</div>
                  <div className="h4 mb-0 fw-bold text-danger">{stats.admins}</div>
                </div>
                <i className="bi bi-shield-lock display-6 text-danger opacity-50"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className={esAdmin() ? 'col-md-3' : 'col-md-4'}>
              <div className="input-group">
                <span className="input-group-text bg-transparent border-end-0">
                  <i className="bi bi-search text-muted"></i>
                </span>
                <input
                  type="text" className="form-control border-start-0"
                  placeholder="Buscar por nombre, usuario, cédula..."
                  value={filtros.buscar}
                  onChange={e => setFiltros({...filtros, buscar: e.target.value})}
                />
              </div>
            </div>
            {esAdmin() && (
              <div className="col-md-2">
                <select
                  className="form-select"
                  value={filtros.sucursal_id}
                  onChange={e => setFiltros({...filtros, sucursal_id: e.target.value})}
                >
                  <option value="">Todas las sucursales</option>
                  {sucursales.filter(s => s.activo).map(s => (
                    <option key={s.id} value={s.id}>
                      {s.nombre}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-md-2">
              <select
                className="form-select"
                value={filtros.rol_id}
                onChange={e => setFiltros({...filtros, rol_id: e.target.value})}
              >
                <option value="">Todos los roles</option>
                {roles.map(r => (
                  <option key={r.id} value={r.id}>{getRolLabel(r.nombre)}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={filtros.activo}
                onChange={e => setFiltros({...filtros, activo: e.target.value})}
              >
                <option value="">Todos</option>
                <option value="true">Activos</option>
                <option value="false">Inactivos</option>
              </select>
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => setFiltros({ buscar: '', rol_id: '', activo: '', sucursal_id: '' })}
              >
                <i className="bi bi-x-circle me-1"></i> Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de Usuarios */}
      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th className="border-0">Usuario</th>
                <th className="border-0">Rol</th>
                <th className="border-0">Sucursal</th>
                <th className="border-0 d-none d-md-table-cell">Contacto</th>
                <th className="border-0">Estado</th>
                <th className="border-0 d-none d-lg-table-cell">Último Login</th>
                <th className="border-0 text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-5">
                    <div className="spinner-border text-primary"></div>
                  </td>
                </tr>
              ) : usuarios.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    <i className="bi bi-people display-4 d-block mb-2"></i>
                    No se encontraron usuarios
                  </td>
                </tr>
              ) : usuarios.map(u => (
                <tr key={u.id} className={!u.activo ? 'opacity-50' : ''}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle text-white fw-bold"
                        style={{
                          width: 38, height: 38, fontSize: '0.85rem',
                          background: `hsl(${(u.nombre_completo || '').charCodeAt(0) * 15 % 360}, 60%, 50%)`
                        }}
                      >
                        {(u.nombre_completo || 'U').substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="fw-semibold">{u.nombre_completo}</div>
                        <div className="text-muted small">@{u.username}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge bg-${getRolBadge(u.rol_nombre)}`}>
                      {getRolLabel(u.rol_nombre)}
                    </span>
                  </td>
                  <td>
                    <span className="small">
                      <i className="bi bi-shop me-1 text-muted"></i>
                      {u.sucursal_nombre || 'Sin asignar'}
                    </span>
                  </td>
                  <td className="d-none d-md-table-cell">
                    <div className="small">
                      {u.email && <div><i className="bi bi-envelope me-1 text-muted"></i>{u.email}</div>}
                      {u.telefono && <div><i className="bi bi-telephone me-1 text-muted"></i>{u.telefono}</div>}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${u.activo ? 'bg-success' : 'bg-secondary'} bg-opacity-75`}>
                      {u.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="d-none d-lg-table-cell small text-muted">
                    {u.ultimo_login
                      ? new Date(u.ultimo_login).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : 'Nunca'}
                  </td>
                  <td className="text-end">
                    <div className="btn-group btn-group-sm">
                      <button className="btn btn-outline-primary" onClick={() => openDetail(u)} title="Ver detalle">
                        <i className="bi bi-eye"></i>
                      </button>
                      <button className="btn btn-outline-primary" onClick={() => openEdit(u)} title="Editar">
                        <i className="bi bi-pencil"></i>
                      </button>
                      {/* No mostrar desactivar para: super_admin, ni para uno mismo */}
                      {u.rol_nombre !== 'super_admin' && u.id !== currentUser?.id && (
                        <button
                          className={`btn ${u.activo ? 'btn-outline-warning' : 'btn-outline-success'}`}
                          onClick={() => toggleActivo(u)}
                          title={u.activo ? 'Desactivar' : 'Activar'}
                        >
                          <i className={`bi ${u.activo ? 'bi-person-dash' : 'bi-person-check'}`}></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Crear/Editar */}
      {showModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowModal(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    <i className={`bi ${editing ? 'bi-pencil' : 'bi-person-plus-fill'} me-2`}></i>
                    {editing ? 'Editar Usuario' : 'Nuevo Usuario'}
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)} />
                </div>
                <form onSubmit={handleSave}>
                  <div className="modal-body">
                    <div className="row g-3">
                      {/* Datos de acceso */}
                      <div className="col-12">
                        <h6 className="fw-bold text-primary mb-0">
                          <i className="bi bi-key me-1"></i> Datos de Acceso
                        </h6>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold">Usuario *</label>
                        <input
                          type="text" className="form-control"
                          value={form.username}
                          onChange={e => setForm({...form, username: e.target.value.toLowerCase().replace(/\s/g, '')})}
                          placeholder="nombre.apellido"
                          required autoFocus
                          disabled={!!editing}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold">Email</label>
                        <input
                          type="email" className="form-control"
                          value={form.email}
                          onChange={e => setForm({...form, email: e.target.value})}
                          placeholder="usuario@empresa.com"
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold">
                          Contraseña {editing ? '(dejar vacío para no cambiar)' : '*'}
                        </label>
                        <input
                          type="password" className="form-control"
                          value={form.password}
                          onChange={e => setForm({...form, password: e.target.value})}
                          placeholder={editing ? '••••••' : 'Min. 6 caracteres'}
                          required={!editing}
                          minLength={form.password ? 6 : undefined}
                        />
                      </div>

                      {/* Datos personales */}
                      <div className="col-12 mt-3">
                        <h6 className="fw-bold text-primary mb-0">
                          <i className="bi bi-person me-1"></i> Datos Personales
                        </h6>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Nombre Completo *</label>
                        <input
                          type="text" className="form-control"
                          value={form.nombre_completo}
                          onChange={e => setForm({...form, nombre_completo: e.target.value})}
                          placeholder="Nombres y Apellidos"
                          required
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small fw-semibold">Cédula</label>
                        <input
                          type="text" className="form-control"
                          value={form.cedula}
                          onChange={e => setForm({...form, cedula: e.target.value.replace(/\D/g, '')})}
                          placeholder="1712345678"
                          maxLength={10}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small fw-semibold">Teléfono</label>
                        <input
                          type="text" className="form-control"
                          value={form.telefono}
                          onChange={e => setForm({...form, telefono: e.target.value})}
                          placeholder="0991234567"
                        />
                      </div>

                      {/* Rol y Sucursal */}
                      <div className="col-12 mt-3">
                        <h6 className="fw-bold text-primary mb-0">
                          <i className="bi bi-shield me-1"></i> Rol y Sucursal
                        </h6>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Rol *</label>
                        <select
                          className="form-select"
                          value={form.rol_id}
                          onChange={e => setForm({...form, rol_id: e.target.value})}
                          required
                        >
                          <option value="">-- Seleccionar Rol --</option>
                          {roles.map(r => (
                            <option key={r.id} value={r.id}>
                              {getRolLabel(r.nombre)} - {r.descripcion}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Sucursal Asignada *</label>
                        <select
                          className="form-select"
                          value={form.sucursal_id}
                          onChange={e => setForm({...form, sucursal_id: e.target.value})}
                          required
                        >
                          <option value="">-- Seleccionar Sucursal --</option>
                          {sucursales.filter(s => s.activo).map(s => (
                            <option key={s.id} value={s.id}>
                              {s.nombre} {s.es_matriz ? '(Matriz)' : ''} - {s.ciudad}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Estado */}
                      <div className="col-12">
                        <div className="form-check form-switch mt-2">
                          <input
                            type="checkbox" className="form-check-input"
                            id="userActivo" checked={form.activo}
                            onChange={e => setForm({...form, activo: e.target.checked})}
                          />
                          <label className="form-check-label fw-semibold" htmlFor="userActivo">
                            Usuario Activo
                          </label>
                          <div className="form-text">
                            Los usuarios inactivos no pueden iniciar sesión en el sistema
                          </div>
                        </div>
                      </div>

                      {/* Resumen de permisos del rol seleccionado */}
                      {form.rol_id && (
                        <div className="col-12">
                          <div className="alert alert-info py-2 mb-0">
                            <i className="bi bi-info-circle me-1"></i>
                            <strong>Permisos del rol:</strong>{' '}
                            {(() => {
                              const rol = roles.find(r => r.id === parseInt(form.rol_id));
                              if (!rol) return 'Sin información';
                              const permisos = rol.permisos || [];
                              if (permisos.includes('*')) return 'Acceso total al sistema';
                              return permisos.join(', ') || 'Sin permisos asignados';
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? (
                        <><span className="spinner-border spinner-border-sm me-1"></span> Guardando...</>
                      ) : (
                        <><i className="bi bi-check-lg me-1"></i> {editing ? 'Actualizar' : 'Crear Usuario'}</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal Detalle */}
      {showDetailModal && selectedUser && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowDetailModal(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title">
                    <i className="bi bi-person-badge me-2 text-primary"></i>
                    Detalle de Usuario
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowDetailModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="text-center mb-4">
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold mb-2"
                      style={{
                        width: 64, height: 64, fontSize: '1.5rem',
                        background: `hsl(${(selectedUser.nombre_completo || '').charCodeAt(0) * 15 % 360}, 60%, 50%)`
                      }}
                    >
                      {(selectedUser.nombre_completo || 'U').substring(0, 2).toUpperCase()}
                    </div>
                    <h5 className="mb-1">{selectedUser.nombre_completo}</h5>
                    <span className={`badge bg-${getRolBadge(selectedUser.rol_nombre)}`}>
                      {getRolLabel(selectedUser.rol_nombre)}
                    </span>
                  </div>

                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td className="text-muted fw-semibold" style={{width:'40%'}}>Usuario</td>
                        <td>@{selectedUser.username}</td>
                      </tr>
                      <tr>
                        <td className="text-muted fw-semibold">Email</td>
                        <td>{selectedUser.email || '-'}</td>
                      </tr>
                      <tr>
                        <td className="text-muted fw-semibold">Cédula</td>
                        <td>{selectedUser.cedula || '-'}</td>
                      </tr>
                      <tr>
                        <td className="text-muted fw-semibold">Teléfono</td>
                        <td>{selectedUser.telefono || '-'}</td>
                      </tr>
                      <tr>
                        <td className="text-muted fw-semibold">Sucursal</td>
                        <td>{selectedUser.sucursal_nombre || '-'}</td>
                      </tr>
                      <tr>
                        <td className="text-muted fw-semibold">Estado</td>
                        <td>
                          <span className={`badge ${selectedUser.activo ? 'bg-success' : 'bg-secondary'}`}>
                            {selectedUser.activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted fw-semibold">Último Login</td>
                        <td>
                          {selectedUser.ultimo_login
                            ? new Date(selectedUser.ultimo_login).toLocaleString('es-EC')
                            : 'Nunca ha ingresado'}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted fw-semibold">Creado</td>
                        <td>{new Date(selectedUser.created_at).toLocaleString('es-EC')}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button className="btn btn-outline-primary" onClick={() => { setShowDetailModal(false); openEdit(selectedUser); }}>
                    <i className="bi bi-pencil me-1"></i> Editar
                  </button>
                  <button className="btn btn-light" onClick={() => setShowDetailModal(false)}>
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Usuarios;
